import React, { HTMLInputTypeAttribute, useMemo } from 'react';
import { cn } from '../../../utils/classNames';
import { useTextInput } from '../../../hooks/useTextInput';

interface FormInputProps {
  name: string;
  label: string;
  value: string;
  onChange: (name: string, value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  type?: HTMLInputTypeAttribute;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  debounceMs?: number;
  validateInput?: (value: string) => boolean;
  autoComplete?: string;
  spellCheck?: boolean;
  'data-testid'?: string;
  'aria-invalid'?: boolean | 'false' | 'true' | 'grammar' | 'spelling';
  'aria-describedby'?: string;
}

/**
 * 統一入力コンポーネント
 *
 * useTextInputフックを必須で使用し、IME処理を統一します。
 * すべてのテキスト入力で一貫した動作を保証します。
 */
export const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  className,
  debounceMs = 0,
  validateInput,
  autoComplete = 'off',
  spellCheck = false,
  'data-testid': testId,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}) => {
  // useTextInputフックを使用してIME処理と状態管理を統一
  const textInput = useTextInput({
    initialValue: value,
    onValueChange: newValue => onChange(name, newValue),
    debounceMs,
    validateInput,
    autoComplete,
    spellCheck,
  });

  // value propの変更を監視してuseTextInputの値を同期
  const prevValueRef = React.useRef<string>(value);
  React.useEffect(() => {
    if (prevValueRef.current !== value && textInput.value !== value) {
      textInput.setValue(value);
    }
    prevValueRef.current = value;
  }, [value, textInput.setValue]);

  const inputId = useMemo(() => `form-input-${name}`, [name]);

  const inputClasses = cn(
    'w-full px-4 py-3 border rounded-lg min-h-[44px] touch-target-input',
    'text-gray-900 dark:text-white',
    'bg-gray-50 dark:bg-gray-700',
    'focus:ring-green-500 focus:border-primary-500',
    !error ? 'border-gray-300 dark:border-gray-600' : 'border-red-300 dark:border-red-600',
    disabled && 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed',
    className
  );

  const labelClasses = cn('block text-sm font-medium mb-1', 'text-gray-700 dark:text-gray-300');

  return (
    <div>
      <label htmlFor={inputId} className={labelClasses}>
        {label}
        {required && <span className='text-red-500 ml-1'>*</span>}
      </label>

      <input
        type={type}
        name={name}
        id={inputId}
        {...textInput.inputProps}
        onBlur={onBlur}
        onFocus={onFocus}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={inputClasses}
        data-testid={testId}
        aria-describedby={
          ariaDescribedBy ||
          (error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined)
        }
        aria-invalid={ariaInvalid !== undefined ? ariaInvalid : !!error}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
      />

      {error && (
        <p id={`${inputId}-error`} className='mt-1 text-sm text-red-600 dark:text-red-400'>
          {error}
        </p>
      )}

      {helperText && !error && (
        <p id={`${inputId}-helper`} className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default FormInput;
